// TeacherMedicationsSheet — read-only list of a child's active
// medications + the recent administration log + a "Record dose"
// affordance per medication that opens MedicationLogSheet.

import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Pill, Warning } from "@phosphor-icons/react";
import {
  formatAdministeredAt,
  isAuthorizationActiveToday,
  listAdministrationsForChild,
  listAuthorizationsForChild,
  routeLabel,
} from "@/lib/medications";
import MedicationLogSheet from "@/components/medications/MedicationLogSheet";

export default function TeacherMedicationsSheet({
  open,
  onOpenChange,
  child,           // {id, organization_id, first_name, last_name}
  administeredBy,  // teacher profile id
}) {
  const [auths, setAuths] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logSheet, setLogSheet] = useState({ open: false, auth: null });

  const refresh = async () => {
    if (!child?.id) return;
    setLoading(true);
    try {
      const [a, l] = await Promise.all([
        listAuthorizationsForChild(child.id, { activeOnly: false }),
        listAdministrationsForChild(child.id, { limit: 10 }),
      ]);
      setAuths(a);
      setLog(l);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, child?.id]);

  const active = auths.filter(isAuthorizationActiveToday);
  const archived = auths.filter((a) => !isAuthorizationActiveToday(a));

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          data-testid="teacher-medications-sheet"
          side="bottom"
          className="max-h-[88dvh] overflow-y-auto rounded-t-3xl border-t border-neutral-200 sm:max-w-md sm:mx-auto"
        >
          <SheetHeader className="text-left">
            <SheetTitle className="font-heading text-xl flex items-center gap-2">
              <Pill size={18} weight="duotone" className="text-primary-hover" />
              Medications
            </SheetTitle>
            <SheetDescription>
              {[child?.first_name, child?.last_name].filter(Boolean).join(" ")}
              {" "}· authorised by parent. You can record each dose given.
            </SheetDescription>
          </SheetHeader>

          {loading && <p className="mt-4 text-sm text-neutral-500">Loading…</p>}

          {!loading && active.length === 0 && (
            <p
              data-testid="teacher-medications-empty"
              className="mt-4 rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-4 text-center text-sm text-neutral-500"
            >
              No active medications authorised by parent.
            </p>
          )}

          {!loading && active.length > 0 && (
            <ul className="mt-4 space-y-2">
              {active.map((a) => (
                <li
                  key={a.id}
                  data-testid={`teacher-medications-row-${a.id}`}
                  className="rounded-2xl border border-primary/20 bg-primary/5 p-3"
                >
                  <p className="flex flex-wrap items-baseline gap-1 text-sm font-semibold text-neutral-900">
                    {a.medication_name}
                    <span className="text-[11px] font-normal text-neutral-500">
                      · {a.dosage} · {routeLabel(a.route)}
                    </span>
                  </p>
                  {a.schedule && (
                    <p className="mt-0.5 text-[12px] text-neutral-700">{a.schedule}</p>
                  )}
                  {a.warnings && (
                    <p className="mt-1 inline-flex items-start gap-1 rounded-xl border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
                      <Warning size={11} weight="duotone" className="mt-0.5" aria-hidden />
                      {a.warnings}
                    </p>
                  )}
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      data-testid={`teacher-medications-log-${a.id}`}
                      onClick={() => setLogSheet({ open: true, auth: a })}
                      className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-hover"
                    >
                      <Pill size={12} weight="duotone" />
                      Record dose
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {archived.length > 0 && (
            <section className="mt-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                Archived
              </p>
              <ul className="mt-1 space-y-1">
                {archived.map((a) => (
                  <li
                    key={a.id}
                    className="rounded-xl border border-neutral-200 bg-neutral-50/40 px-2 py-1 text-[12px] text-neutral-500"
                  >
                    {a.medication_name} · {a.dosage}
                    {a.ends_on ? ` · ended ${a.ends_on}` : " · withdrawn"}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {log.length > 0 && (
            <section className="mt-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                Recent log
              </p>
              <ul className="mt-1 space-y-1 text-[12px] text-neutral-700">
                {log.map((r) => (
                  <li
                    key={r.id}
                    className="rounded-xl border border-neutral-200 bg-white px-2 py-1"
                  >
                    <strong>{r.medication_name}</strong> · {r.dose_given} ·{" "}
                    {formatAdministeredAt(r.administered_at)}
                    {r.administrator?.full_name
                      ? ` · ${r.administrator.full_name}`
                      : ""}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </SheetContent>
      </Sheet>

      <MedicationLogSheet
        open={logSheet.open}
        onOpenChange={(o) => setLogSheet((s) => ({ ...s, open: o }))}
        authorization={logSheet.auth}
        child={child}
        administeredBy={administeredBy}
        onSaved={() => refresh()}
      />
    </>
  );
}
